import { visuallyHidden } from "@mui/utils";
import { REHAB_TRIGGERING_EVENT } from "core/consts";
import { PENSION_INSURANCE_NUMBER_CHARACTER_LIMIT } from "core/schemas/shared";
import ConnectedCheckboxGroup, {
  CheckboxGroupProps,
} from "ds_legacy/components/CheckboxGroup";
import CheckboxInputField from "ds_legacy/components/CheckboxInputField";
import { margin, sizing } from "ds_legacy/materials/metrics";
import {
  DatePickerWithLabel,
  LARGE_INPUT_WIDTH,
  PatientFormSubheading,
  SMALL_INPUT_MIN_WIDTH,
  Section,
  SectionRow,
  TextInputWithLabel,
} from "dsl/ecosystems/PatientForms/components";
import { useTranslations } from "translations";

export const IntroSection = () => {
  const translations = useTranslations();

  const options: CheckboxGroupProps["items"] = [
    {
      id: REHAB_TRIGGERING_EVENT.SURGERY,
      label: translations.patientForms.drvMedicalForm.sectionZero.surgery,
    },
    {
      id: REHAB_TRIGGERING_EVENT.ACUTE_EVENT,
      label: translations.patientForms.drvMedicalForm.sectionZero.acuteEvent,
    },
    {
      id: REHAB_TRIGGERING_EVENT.RADIATION,
      label: translations.patientForms.drvMedicalForm.sectionZero.radiation,
    },
    {
      id: REHAB_TRIGGERING_EVENT.ANTINEOPLASTIC_THERAPY,
      label:
        translations.patientForms.drvMedicalForm.sectionZero
          .antineoplasticTherapy,
    },
    {
      id: REHAB_TRIGGERING_EVENT.NOT_APPLICABLE,
      label: translations.patientForms.drvMedicalForm.sectionZero.notApplicable,
    },
    {
      id: REHAB_TRIGGERING_EVENT.ACCIDENT_WORK,
      label: translations.patientForms.drvMedicalForm.sectionZero.accidentWork,
    },
    {
      id: REHAB_TRIGGERING_EVENT.THIRD_PARTY_DAMAGE,
      label:
        translations.patientForms.drvMedicalForm.sectionZero.thirdPartyDamage,
    },
  ];

  return (
    <Section>
      <SectionRow>
        <TextInputWithLabel
          bold
          characterLimit={PENSION_INSURANCE_NUMBER_CHARACTER_LIMIT}
          elementName="statutory_pension_insurance_number"
          label={
            translations.patientForms.drvMedicalForm.sectionZero.insuranceNumber
          }
          large
        />
      </SectionRow>
      <PatientFormSubheading style={{ margin: margin(0, 0, -1) }}>
        {
          translations.patientForms.drvMedicalForm.sectionZero
            .subtitleHospitalAddress
        }
      </PatientFormSubheading>
      <SectionRow>
        <TextInputWithLabel
          bold
          elementName="hospital_name_address"
          label={translations.patientForms.drvMedicalForm.addressHospital}
          labelWrapperSx={{ width: "100%" }}
          large
        />
      </SectionRow>
      <PatientFormSubheading style={{ margin: margin(0, 0, -1) }}>
        {
          translations.patientForms.drvMedicalForm.sectionZero
            .subtitleContactPerson
        }
      </PatientFormSubheading>
      <SectionRow>
        <TextInputWithLabel
          bold
          elementName="social_worker_name_number"
          label={
            translations.patientForms.drvMedicalForm.sectionZero
              .contactPersonInformation
          }
          labelWrapperSx={{ width: "100%" }}
          large
        />
      </SectionRow>
      <PatientFormSubheading style={{ margin: margin(0, 0, -1) }}>
        {translations.patientForms.drvMedicalForm.sectionZero.subtitleAhb}
      </PatientFormSubheading>
      <SectionRow>
        <TextInputWithLabel
          bold
          elementName="preferred_rehab_clinic"
          label={
            translations.patientForms.drvMedicalForm.sectionZero.addressAhb
          }
          labelWrapperSx={{ width: "100%" }}
          large
        />
      </SectionRow>
      <PatientFormSubheading withBottomPadding>
        {translations.patientForms.drvMedicalForm.sectionZero.typeOfProcedure}
      </PatientFormSubheading>
      <CheckboxInputField
        elementName="pension_procedure_direct"
        label={
          translations.patientForms.drvMedicalForm.sectionZero.directDischarge
        }
      />
      <CheckboxInputField
        elementName="pension_procedure_after_examination"
        label={
          translations.patientForms.drvMedicalForm.sectionZero
            .instructionAfterExamination
        }
      />
      <PatientFormSubheading withBottomPadding>
        {
          translations.patientForms.drvMedicalForm.sectionZero
            .subtitleSuggestedRehab
        }
      </PatientFormSubheading>
      <CheckboxInputField
        elementName="full_reha"
        label={translations.patientForms.drvMedicalForm.sectionZero.static}
      />
      <CheckboxInputField
        elementName="day_reha"
        label={translations.patientForms.drvMedicalForm.sectionZero.mobile}
      />
      <PatientFormSubheading style={{ margin: margin(0, 0, -1) }}>
        {
          translations.patientForms.drvMedicalForm.sectionZero
            .subtitlePatientInformation
        }
      </PatientFormSubheading>
      <SectionRow>
        <TextInputWithLabel
          bold
          elementName="patient_name"
          label={
            translations.patientForms.drvMedicalForm.sectionZero.patientName
          }
          large
        />
        <DatePickerWithLabel
          bold
          elementName="patient_birthdate"
          inputSx={{
            minWidth: SMALL_INPUT_MIN_WIDTH,
            width: "100%",
          }}
          label={
            translations.patientForms.drvMedicalForm.sectionZero
              .dateBirthPatient
          }
          wrapperSx={{ width: LARGE_INPUT_WIDTH }}
        />
      </SectionRow>
      <SectionRow>
        <TextInputWithLabel
          bold
          elementName="patient_street_housenumber"
          label={
            translations.patientForms.drvMedicalForm.sectionZero.addressPatient
          }
          large
        />
      </SectionRow>
      <SectionRow>
        <TextInputWithLabel
          bold
          elementName="patient_zipcode"
          label={
            translations.patientForms.drvMedicalForm.sectionZero.zipCodePatient
          }
        />
        <TextInputWithLabel
          bold
          elementName="patient_city"
          label={translations.patientForms.drvMedicalForm.sectionZero.city}
          large
        />
      </SectionRow>
      <PatientFormSubheading style={{ margin: margin(0, 0, -1) }}>
        {translations.patientForms.drvMedicalForm.sectionZero.subtitleHospital}
      </PatientFormSubheading>
      <SectionRow>
        <DatePickerWithLabel
          bold
          elementName="admission_date"
          inputSx={{
            minWidth: SMALL_INPUT_MIN_WIDTH,
            width: "100%",
          }}
          label={
            translations.patientForms.drvMedicalForm.sectionZero.intakeDate
          }
          wrapperSx={{ width: LARGE_INPUT_WIDTH }}
        />
      </SectionRow>
      <SectionRow>
        <DatePickerWithLabel
          bold
          elementName="rehab_triggering_event_date"
          inputSx={{
            minWidth: SMALL_INPUT_MIN_WIDTH,
            width: "100%",
          }}
          label={
            translations.patientForms.drvMedicalForm.sectionZero.ahbEventDate
          }
          wrapperSx={{ width: LARGE_INPUT_WIDTH }}
        />
      </SectionRow>
      <SectionRow>
        <ConnectedCheckboxGroup
          elementName="rehab_triggering_event"
          formGroupStyle={{ gap: sizing(1) }}
          items={options}
          label={
            translations.patientForms.drvMedicalForm.sectionZero
              .ahbEventCheckboxGroupLabel
          }
          labelStyle={visuallyHidden}
        />
      </SectionRow>
      <SectionRow>
        <DatePickerWithLabel
          bold
          elementName="discharge_date"
          inputSx={{
            minWidth: SMALL_INPUT_MIN_WIDTH,
            width: "100%",
          }}
          label={
            translations.patientForms.drvMedicalForm.sectionZero.dischargeDate
          }
          wrapperSx={{ width: LARGE_INPUT_WIDTH }}
        />
        <DatePickerWithLabel
          bold
          elementName="transfer_date"
          inputSx={{
            minWidth: SMALL_INPUT_MIN_WIDTH,
            width: "100%",
          }}
          label={
            translations.patientForms.drvMedicalForm.sectionZero.ahbStartDate
          }
          wrapperSx={{ width: LARGE_INPUT_WIDTH }}
        />
      </SectionRow>
    </Section>
  );
};
